import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { PlayCircle } from 'lucide-react';

export type LiveBannerEvent = {
  id: string;
  title: string;
  event_url: string | null;
  is_started: boolean | null;
  is_ended: boolean | null;
};

export default function LiveEventBanner() {
  const [live, setLive] = useState<LiveBannerEvent | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchLive = async () => {
      try {
        const { data, error } = await supabase
          .from('live_events')
          .select('id, title, event_url, is_started, is_ended')
          .eq('is_started', true)
          .eq('is_ended', false)
          .order('event_date', { ascending: true })
          .limit(1);
        if (error) throw error;
        if (!cancelled) setLive((data as any[])?.[0] || null);
      } catch (e) {
        // fail silently
        if (!cancelled) setLive(null);
      }
    };

    fetchLive();
    const t = setInterval(fetchLive, 15000); // refresh every 15s
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  if (!live) return null;

  const joinHref = live.event_url || '#';

  return (
    <div className="fixed z-40 pointer-events-none left-3 right-20 top-3 sm:left-1/2 sm:right-auto sm:-translate-x-1/2 sm:top-4 px-1 sm:px-2">
      <div className="mx-auto pointer-events-auto max-w-[95vw] sm:max-w-2xl">
        <div className="glow-ring-box rounded-full bg-[#0d1117]/80 backdrop-blur-md border border-white/10 shadow-xl pl-3 pr-2 py-1.5 sm:py-2 flex items-center gap-2 sm:gap-3">
          {/* Glowing red dot instead of icon */}
          <span className="relative flex h-4 w-4 sm:h-3.5 sm:w-3.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-4 w-4 sm:h-3.5 sm:w-3.5 bg-red-500"></span>
          </span>
          {/* Title: marquee on mobile, truncated static on desktop */}
          <div className="block sm:hidden max-w-[48vw] marquee-container">
            <div className="marquee-track text-white font-semibold text-sm">
              <span className="pr-8">{live.title}</span>
              <span className="pr-8" aria-hidden>{live.title}</span>
            </div>
          </div>
          <div className="hidden sm:block text-white font-semibold truncate max-w-[60vw] sm:text-base">{live.title}</div>
          <a
            href={joinHref}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-auto inline-flex items-center gap-2 px-3 py-1.5 rounded-full font-semibold text-gray-900 bg-[#c9fffc] hover:bg-[#a0fcf9] transition agent-glow agent-pulse"
          >
            <PlayCircle className="w-4 h-4"/> <span className="hidden sm:inline">Join Now</span><span className="inline sm:hidden">Join</span>
          </a>
        </div>
      </div>
    </div>
  );
}
